import React, { useEffect, useRef, useState } from 'react';
import { ExpandMoreOutlined, CheckOutlined } from '../icons';
import './Dropdown.css';

// Dropdown genérico (trigger em pílula + menu flutuante com item ativo
// marcado por check) usado tanto no seletor de concurso do topbar quanto
// no filtro de matéria das questões livres, para manter a mesma UI nos dois.
function Dropdown({
  value,
  onChange,
  options,
  headerLabel,
  icon,
  placeholder = 'Selecione',
  disabled = false,
  align = 'left',
  className = '',
  triggerClassName = '',
  ariaLabel,
  searchable = false,
  searchPlaceholder = 'Buscar...',
}) {
  const [open, setOpen] = useState(false);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const rootRef = useRef(null);

  const markImageFailed = (optionValue) => {
    setFailedImages((prev) => new Set(prev).add(String(optionValue)));
  };

  const renderOptionVisual = (option, iconClassName, imageClassName) => {
    if (option.image && !failedImages.has(String(option.value))) {
      return (
        <img
          src={option.image}
          alt=""
          className={imageClassName}
          onError={() => markImageFailed(option.value)}
        />
      );
    }
    if (option.icon) {
      return <span className={iconClassName}>{option.icon}</span>;
    }
    return null;
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  const visibleOptions = searchable && searchTerm.trim()
    ? options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.trim().toLowerCase()))
    : options;

  const handleSelect = (option) => {
    if (String(option.value) !== String(value)) onChange(option.value);
    setOpen(false);
  };

  return (
    <div className={`dropdown ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`dropdown-trigger ${triggerClassName} ${open ? 'dropdown-trigger--open' : ''}`.trim()}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {selectedOption
          ? renderOptionVisual(selectedOption, 'dropdown-trigger-icon', 'dropdown-trigger-image')
          : icon && <span className="dropdown-trigger-icon">{icon}</span>}
        <span className="dropdown-trigger-label">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {!disabled && (
          <ExpandMoreOutlined className="dropdown-trigger-chevron" style={{ fontSize: 18 }} />
        )}
      </button>

      {open && !disabled && (
        <div className={`dropdown-menu dropdown-menu--${align}`} role="listbox">
          {headerLabel && <span className="dropdown-menu-header">{headerLabel}</span>}
          {searchable && (
            <input
              type="text"
              className="dropdown-menu-search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          )}
          <div className="dropdown-menu-list">
            {visibleOptions.length === 0 && (
              <span className="dropdown-menu-empty">Nenhum resultado encontrado</span>
            )}
            {visibleOptions.map((option) => {
              const isActive = String(option.value) === String(value);
              return (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={isActive}
                  className={`dropdown-menu-item ${isActive ? 'dropdown-menu-item--active' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className="dropdown-menu-item-label">
                    {renderOptionVisual(option, 'dropdown-menu-item-icon', 'dropdown-menu-item-image')}
                    {option.label}
                  </span>
                  {isActive && <CheckOutlined className="dropdown-menu-item-check" style={{ fontSize: 16 }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dropdown;
